import { useDebouncedValue } from "@mantine/hooks";
import { useCallback, useMemo, useReducer } from "react";
import { useSearchParams } from "react-router-dom";
import {
  SystemEmailCategory,
  SystemEmailMode,
} from "../../services/admin/systemEmails.service";
import { useSystemEmailRegistry } from "./useSystemEmails";

const VALID_TABS = ["registry", "queue"] as const;
export type SystemEmailsTab = (typeof VALID_TABS)[number];
export interface CatalogueState {
  search: string;
  category: SystemEmailCategory | null;
  mode: SystemEmailMode | null;
}
export type CatalogueAction =
  | { type: "search"; value: string }
  | { type: "category"; value: SystemEmailCategory | null }
  | { type: "mode"; value: SystemEmailMode | null };

function reducer(
  state: CatalogueState,
  action: CatalogueAction
): CatalogueState {
  if (action.type === "search") return { ...state, search: action.value };
  if (action.type === "category") return { ...state, category: action.value };
  return { ...state, mode: action.value };
}

export function useSystemEmailsTab() {
  const [searchParams, setSearchParams] = useSearchParams();
  const active = useMemo(() => {
    const tab = searchParams.get("tab") as SystemEmailsTab;
    return VALID_TABS.includes(tab) ? tab : "registry";
  }, [searchParams]);
  const change = useCallback(
    (value: string | null) => {
      if (!value || !VALID_TABS.includes(value as SystemEmailsTab)) return;
      setSearchParams(value === "registry" ? {} : { tab: value }, {
        replace: true,
      });
    },
    [setSearchParams]
  );
  return { active, change };
}

export function useSystemEmailCatalogue() {
  const [state, dispatch] = useReducer(reducer, {
    search: "",
    category: null,
    mode: null,
  });
  const [search] = useDebouncedValue(state.search.trim().toLowerCase(), 250);
  const registry = useSystemEmailRegistry({
    category: state.category ?? undefined,
    mode: state.mode ?? undefined,
  });
  const entries = useFilteredSystemEmails(registry.data ?? [], search);
  const filtered = useMemo(
    () => Boolean(search || state.category || state.mode),
    [search, state]
  );
  return { state, dispatch, registry, entries, filtered };
}

function useFilteredSystemEmails(
  entries: ReturnType<typeof useSystemEmailRegistry>["data"],
  search: string
) {
  return useMemo(
    () =>
      (entries ?? []).filter((entry) =>
        [entry.label, entry.subject, entry.description].some((value) =>
          value.toLowerCase().includes(search)
        )
      ),
    [entries, search]
  );
}
